import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { X, Clock, Play, Square, Plus, Edit2, Trash2, Loader2 } from 'lucide-react';
import { apiService } from '@/lib/api';
import { JobDTO, JobVisitDTO, CreateJobVisitDTO, PatchJobVisitDTO } from '@/types';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { formatInstantInCompanyTimeZone, formatDateOnlyInCompanyTimeZone, formatTimeInCompanyTimeZone } from '@/lib/dateUtils';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { useIsMobile } from '@/hooks/useIsMobile';

interface JobVisitsModalProps {
    isOpen: boolean;
    onClose: () => void;
    job: JobDTO;
    onJobUpdated?: (updatedJob: JobDTO) => void;
}

/** Append Z if the ISO string has no timezone marker (Npgsql legacy timestamp behaviour). */
function ensureUtc(iso: string): string {
    return iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
}

function formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function useLiveElapsed(checkInTime: string | null | undefined): string {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
        if (!checkInTime) { setElapsed(''); return; }
        const update = () => {
            const mins = Math.floor((Date.now() - new Date(ensureUtc(checkInTime)).getTime()) / 60000);
            setElapsed(formatDuration(Math.max(0, mins)));
        };
        update();
        const id = setInterval(update, 60000);
        return () => clearInterval(id);
    }, [checkInTime]);

    return elapsed;
}

export function JobVisitsModal({ isOpen, onClose, job, onJobUpdated }: JobVisitsModalProps) {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { userRole } = useCurrentUser();
    const timezone = useCompanyTimezone();
    const isMobile = useIsMobile();
    const isTechnician = userRole === 'technician';

    // Sub-modal states
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingVisit, setEditingVisit] = useState<JobVisitDTO | null>(null);
    const [deletingVisitId, setDeletingVisitId] = useState<string | null>(null);

    // Manual entry form state
    const [newCheckIn, setNewCheckIn] = useState('');
    const [newCheckOut, setNewCheckOut] = useState('');
    const [newNotes, setNewNotes] = useState('');

    // Edit form state
    const [editCheckIn, setEditCheckIn] = useState('');
    const [editCheckOut, setEditCheckOut] = useState('');
    const [editNotes, setEditNotes] = useState('');

    const isSubModalOpen = showAddForm || !!editingVisit || !!deletingVisitId;
    const layer = useModalLayer({
        enabled: isOpen && !isSubModalOpen,
        onClose,
        historyState: { jobVisitsModal: true },
    });

    // ── Data ────────────────────────────────────────────────────────
    const { data: visits = [], isLoading } = useQuery({
        queryKey: ['job-visits', job.id],
        queryFn: () => apiService.getJobVisits(job.id),
        enabled: isOpen,
    });

    const activeVisit: JobVisitDTO | undefined =
        job.active_visit ??
        visits.find(v => v.check_out_time === null);

    const elapsed = useLiveElapsed(activeVisit?.check_in_time ?? null);

    const completedVisits = visits.filter(v => v.check_out_time !== null);
    const totalMinutes = completedVisits.reduce((sum, v) => sum + (v.duration_minutes ?? 0), 0);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['job-visits', job.id] });
        queryClient.invalidateQueries({ queryKey: ['job', job.id] });
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        if (onJobUpdated) apiService.getJobById(job.id).then(onJobUpdated);
    };

    // ── Mutations ────────────────────────────────────────────────────
    const checkInMutation = useMutation({
        mutationFn: () => apiService.checkInToJob(job.id),
        onSuccess: () => {
            invalidate();
            addNotification(notificationService.success('Checked in successfully'));
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.details;
            const msg = Array.isArray(detail) ? detail[0] : detail ?? 'Please try again.';
            addNotification(notificationService.error('Check-in failed', msg));
        },
    });

    const checkOutMutation = useMutation({
        mutationFn: (notes: string) => apiService.checkOutFromJob(job.id, { notes: notes || undefined }),
        onSuccess: () => {
            invalidate();
            addNotification(notificationService.success('Checked out successfully'));
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.details;
            const msg = Array.isArray(detail) ? detail[0] : detail ?? 'Please try again.';
            addNotification(notificationService.error('Check-out failed', msg));
        },
    });

    const createVisitMutation = useMutation({
        mutationFn: (data: CreateJobVisitDTO) => apiService.createJobVisit(job.id, data),
        onSuccess: () => {
            invalidate();
            setShowAddForm(false);
            setNewCheckIn(''); setNewCheckOut(''); setNewNotes('');
            addNotification(notificationService.success('Visit entry added'));
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.details;
            const msg = Array.isArray(detail) ? detail[0] : detail ?? 'Please try again.';
            addNotification(notificationService.error('Failed to add visit', msg));
        },
    });

    const patchVisitMutation = useMutation({
        mutationFn: ({ visitId, data }: { visitId: string; data: PatchJobVisitDTO }) =>
            apiService.updateJobVisit(job.id, visitId, data),
        onSuccess: () => {
            invalidate();
            setEditingVisit(null);
            addNotification(notificationService.success('Visit updated'));
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.details;
            const msg = Array.isArray(detail) ? detail[0] : detail ?? 'Please try again.';
            addNotification(notificationService.error('Failed to update visit', msg));
        },
    });

    const deleteVisitMutation = useMutation({
        mutationFn: (visitId: string) => apiService.deleteJobVisit(job.id, visitId),
        onSuccess: () => {
            invalidate();
            setDeletingVisitId(null);
            addNotification(notificationService.success('Visit deleted'));
        },
        onError: () => {
            addNotification(notificationService.error('Failed to delete visit'));
        },
    });

    if (!isOpen) return null;

    const fmtDateTime = (iso: string) =>
        formatInstantInCompanyTimeZone(iso, timezone);

    const fmtDateRange = (startIso: string, endIso: string) => {
        const startDate = formatDateOnlyInCompanyTimeZone(startIso, timezone);
        const endDate = formatDateOnlyInCompanyTimeZone(endIso, timezone);
        const startTime = formatTimeInCompanyTimeZone(startIso, timezone);
        const endTime = formatTimeInCompanyTimeZone(endIso, timezone);

        if (startDate === endDate) {
            return `${startDate}, ${startTime} → ${endTime}`;
        }
        return `${startDate}, ${startTime} → ${endDate}, ${endTime}`;
    };

    const toLocalInput = (iso: string): string => {
        // Parse as UTC (append Z if server omitted timezone), then format in local time
        const d = new Date(ensureUtc(iso));
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const fromLocalInput = (local: string): string => new Date(local).toISOString();

    const openEditForm = (visit: JobVisitDTO) => {
        setEditCheckIn(toLocalInput(visit.check_in_time));
        setEditCheckOut(visit.check_out_time ? toLocalInput(visit.check_out_time) : '');
        setEditNotes(visit.notes ?? '');
        setEditingVisit(visit);
    };

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
            <div
                className={isMobile
                    ? "fixed inset-0 flex flex-col bg-white safe-area-top"
                    : "fixed inset-0 flex items-center justify-center p-4"
                }
                style={layer.style}
            >
                {!isMobile && (
                    <div className="absolute inset-0 bg-black/50" onClick={layer.onClose} />
                )}
                <div className={isMobile
                    ? "flex flex-col flex-1 min-h-0"
                    : "relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                }>
                    {/* ── Header ── */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={layer.onClose}
                                className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Time Log</h2>
                                {totalMinutes > 0 && (
                                    <p className="text-xs text-gray-500">Total: {formatDuration(totalMinutes)}</p>
                                )}
                            </div>
                        </div>
                        {/* Total badge */}
                        {totalMinutes > 0 && (
                            <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                                {formatDuration(totalMinutes)}
                            </span>
                        )}
                    </div>

                    {/* ── Timer section ── */}
                    <div className="px-4 py-4 border-b border-gray-100">
                        {activeVisit ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
                                    <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                                    Currently on site: {elapsed}
                                </div>
                                <button
                                    onClick={() => checkOutMutation.mutate('')}
                                    disabled={checkOutMutation.isPending}
                                    className="w-full py-3 rounded-xl bg-red-600 text-white text-sm font-semibold flex items-center justify-center gap-2 active:bg-red-700 disabled:opacity-50"
                                >
                                    {checkOutMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Square className="h-4 w-4 fill-white" />
                                    )}
                                    Check Out
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => checkInMutation.mutate()}
                                disabled={checkInMutation.isPending || !job.assigned_technician}
                                className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-2 active:bg-blue-700 disabled:opacity-50"
                            >
                                {checkInMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Play className="h-4 w-4 fill-white" />
                                )}
                                Check In
                            </button>
                        )}
                        {!job.assigned_technician && (
                            <p className="text-xs text-amber-600 mt-2 text-center">Assign a technician to enable check-in</p>
                        )}
                    </div>

                    {/* ── Visit list ── */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="px-4 pt-4 pb-2">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Time Logged</h3>
                        </div>

                        {isLoading ? (
                            <div className="flex justify-center py-10">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : visits.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <Clock className="h-10 w-10 text-gray-300 mb-3" />
                                <p className="text-sm font-medium">No visits recorded yet</p>
                            </div>
                        ) : (
                            <div className="px-4 space-y-2 pb-4">
                                {visits.map(visit => (
                                    <div key={visit.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                {visit.check_out_time ? (
                                                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {fmtDateRange(visit.check_in_time, visit.check_out_time)}
                                                            {visit.duration_minutes != null && (
                                                                <span className="ml-1 text-gray-500 font-normal">
                                                                    ({formatDuration(visit.duration_minutes)})
                                                                </span>
                                                            )}
                                                        </p>
                                                        {visit.technician_name && (
                                                            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-md">
                                                                {visit.technician_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                                        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                                                        <p className="text-sm font-medium text-green-700">
                                                            Started {fmtDateTime(visit.check_in_time)} — Active
                                                        </p>
                                                        {visit.technician_name && (
                                                            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-md">
                                                                {visit.technician_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {visit.auto_checked_out && (
                                                    <span className="text-xs text-amber-600">(auto check-out)</span>
                                                )}
                                                {visit.notes && (
                                                    <p className="text-xs text-gray-500 mt-1 italic">"{visit.notes}"</p>
                                                )}
                                            </div>

                                            {/* Admin/Secretary actions */}
                                            {!isTechnician && (
                                                <div className="flex gap-1 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => openEditForm(visit)}
                                                        className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeletingVisitId(visit.id)}
                                                        className="p-2 bg-red-50 text-red-500 rounded-full hover:bg-red-100 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Admin: Add entry button */}
                        {!isTechnician && (
                            <div className="px-4 pb-6">
                                <button
                                    onClick={() => setShowAddForm(true)}
                                    className="w-full py-3 rounded-xl border-2 border-dashed border-gray-300 text-sm text-gray-500 flex items-center justify-center gap-2 hover:border-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Entry
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Edit visit dialog ── */}
                {editingVisit && (
                    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[9999]" onClick={() => setEditingVisit(null)}>
                        <div className="bg-white w-full md:max-w-sm md:mx-4 rounded-t-2xl md:rounded-xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
                            <h3 className="font-semibold text-gray-900">Edit Visit</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Check-in time</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        value={editCheckIn}
                                        onChange={e => setEditCheckIn(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Check-out time</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        value={editCheckOut}
                                        onChange={e => setEditCheckOut(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Notes</label>
                                    <textarea
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                                        rows={2}
                                        value={editNotes}
                                        onChange={e => setEditNotes(e.target.value)}
                                        placeholder="Optional notes..."
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 keyboard-aware-footer">
                                <button
                                    onClick={() => patchVisitMutation.mutate({
                                        visitId: editingVisit.id,
                                        data: {
                                            check_in_time: editCheckIn ? fromLocalInput(editCheckIn) : undefined,
                                            check_out_time: editCheckOut ? fromLocalInput(editCheckOut) : undefined,
                                            notes: editNotes,
                                        }
                                    })}
                                    disabled={patchVisitMutation.isPending}
                                    className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                                >
                                    {patchVisitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Save'}
                                </button>
                                <button
                                    onClick={() => setEditingVisit(null)}
                                    className="px-4 py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Add manual entry dialog ── */}
                {showAddForm && (
                    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[9999]" onClick={() => setShowAddForm(false)}>
                        <div className="bg-white w-full md:max-w-sm md:mx-4 rounded-t-2xl md:rounded-xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
                            <h3 className="font-semibold text-gray-900">Add Visit Entry</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Check-in time <span className="text-red-500">*</span></label>
                                    <input
                                        type="datetime-local"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        value={newCheckIn}
                                        onChange={e => setNewCheckIn(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Check-out time <span className="text-red-500">*</span></label>
                                    <input
                                        type="datetime-local"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        value={newCheckOut}
                                        onChange={e => setNewCheckOut(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Notes</label>
                                    <textarea
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                                        rows={2}
                                        value={newNotes}
                                        onChange={e => setNewNotes(e.target.value)}
                                        placeholder="Optional notes..."
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 keyboard-aware-footer">
                                <button
                                    onClick={() => {
                                        if (!newCheckIn || !newCheckOut) return;
                                        createVisitMutation.mutate({
                                            check_in_time: fromLocalInput(newCheckIn),
                                            check_out_time: fromLocalInput(newCheckOut),
                                            notes: newNotes || undefined,
                                        });
                                    }}
                                    disabled={createVisitMutation.isPending || !newCheckIn || !newCheckOut}
                                    className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                                >
                                    {createVisitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Add Entry'}
                                </button>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="px-4 py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Delete confirm ── */}
                <ConfirmDialog
                    isOpen={!!deletingVisitId}
                    onClose={() => setDeletingVisitId(null)}
                    onConfirm={async () => {
                        if (deletingVisitId) await deleteVisitMutation.mutateAsync(deletingVisitId);
                    }}
                    title="Delete Visit"
                    message="Are you sure you want to delete this visit entry? This cannot be undone."
                    confirmText="Delete"
                    cancelText="Cancel"
                />
            </div>
        </LayerDepthContext.Provider>,
        document.body
    );
}
